import {
  ColumnSortEnum,
  TablePaginationProps,
  TableProps,
  TableState,
  TableStateWithFilters,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";

/**
 * Creates a default PagedResult<T> with rowCount, currentPage, etc.
 * and an empty T[] for the results.
 */
export function tableDefaultData<T>(): PagedResult<T> {
  return {
    currentPage: 1,
    firstRowOnPage: 0,
    lastRowOnPage: 0,
    pageCount: 0,
    pageSize: 10,
    rowCount: 0,
    results: [],
  } as PagedResult<T>;
}

/**
 * Creates a default TableState (search metadata) with page, pageSize, sortBy,
 * and order using the specified values.
 * @param sortBy
 * @param order
 * @param pageSize
 * @returns
 */
export function tableDefaultState(
  sortBy: number = 1,
  order: ColumnSortEnum = ColumnSortEnum.ASC,
  pageSize: number = 10
): TableState {
  return {
    page: 1,
    pageSize: pageSize,
    sortBy: sortBy,
    order: order,
  } as TableState;
}

/**
 * Gets or creates a TableStateWithFilters<TFilter>. This checks the specified
 * storageKey for data and, if found, returns it. If it is not found, it returns
 * the provided tableState.
 * @param storageKey
 * @param tableState
 * @returns
 */
export function CreateInitialTableState<T>(
  storageKey: string,
  tableState: TableState
): TableStateWithFilters<T> {
  let sessionStorageTableState = sessionStorage.getItem(storageKey);
  return sessionStorageTableState
    ? (JSON.parse(sessionStorageTableState) as TableStateWithFilters<T>)
    : ({
        ...tableState,
      } as TableStateWithFilters<T>);
}

//REQUEST
/**
 * Gets the search (filter) data using the provided tableSearch (type TFilter) combined
 * with provided tableState (type TableStateWithFilter<TFilter>). The filter data from
 * TableStateWithFilter<TFilter> is NOT used (only the TableState data is used).
 * @param tableSearch
 * @param tableState
 * @returns
 */
export function GetSearchRequest<T>(
  tableSearch: T,
  tableState: TableStateWithFilters<T>
) {
  return {
    ...tableSearch,
    sort: tableState.sortBy ?? 1,
    order: tableState.order ?? 1,
    page: tableState.page ?? 1,
    pageSize: tableState.pageSize ?? 10,
  };
}

//SESSIONSTORAGE
/**
 * Sets the session storage for the specified storageKey to the
 * specified tableSearch using the TableState from the specified
 * tableState metadata.
 * @param storageKey
 * @param tableSearch
 * @param tableState
 */
export function SetSessionStorageItem<T>(
  storageKey: string,
  tableSearch: T,
  tableState: TableStateWithFilters<T>
) {
  sessionStorage.setItem(
    storageKey,
    JSON.stringify({
      ...tableSearch,
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10,
    })
  );
}

/**
 * Removes the session storage data for the specified storageKey.
 * @param storageKey
 */
export function RemoveSessionStorageItem(storageKey: string) {
  if (sessionStorage.getItem(storageKey)) sessionStorage.removeItem(storageKey);
}

/**
 * Adds the specified pageSizeOptions to the tableProps.
 * @param tableProps
 * @param pageSizeOptions
 * @returns
 */
export const addPageSizeOptionsToTableProps = (
  tableProps: TableProps,
  pageSizeOptions: number[]
): TableProps => {
  return {
    ...tableProps,
    paginationProps: {
      ...tableProps.paginationProps,
      pageSizeOptions,
    } as TablePaginationProps,
  };
};
